import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resourcesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save, Eye, Loader2, Code } from 'lucide-react';
import { CATEGORY_NAMES as CATEGORIES } from '../../constants/resourceCategories';

const NAVY = '#1e3a5f';
const ORANGE = '#ea580c';

const BLOCK_TYPES = [
  { value: 'heading',        label: 'Heading' },
  { value: 'subheading',     label: 'Subheading' },
  { value: 'paragraph',      label: 'Paragraph' },
  { value: 'callout',        label: 'Callout box' },
  { value: 'internal_note',  label: 'C4P Guidance note' },
  { value: 'bullets',        label: 'Bullet list' },
  { value: 'numbered',       label: 'Numbered list' },
  { value: 'email_template', label: 'Email template' },
  { value: 'table',          label: 'Table' },
  { value: 'divider',        label: 'Divider' },
  { value: 'decision_tree',  label: 'Decision tree (JSON)' },
  { value: 'plan_comparison',label: 'Plan comparison (JSON)' },
  { value: 'scenario_cards', label: 'Scenario cards (JSON)' },
];

const COMPLEX_TYPES = ['decision_tree', 'plan_comparison', 'scenario_cards', 'table'];

function newBlock(type) {
  if (type === 'bullets' || type === 'numbered') return { type, items: [{ text: '', active: true }] };
  if (type === 'email_template') return { type, subject: '', content: '' };
  if (type === 'table') return { type, headers: ['Column 1', 'Column 2'], rows: [['', '']] };
  if (type === 'decision_tree') return {
    type,
    questions: [
      { id: 'q1', text: '', yes: { outcome: 'C4P Advanced', color: 'advanced' }, no: 'q2' },
      { id: 'q2', text: '', yes: { outcome: 'C4P Advanced', color: 'advanced' }, no: 'q3' },
      { id: 'q3', text: '', yes: { outcome: 'C4P MTPro', color: 'mtpro' }, no: { outcome: 'C4P Standard', color: 'standard' } },
    ],
  };
  if (type === 'plan_comparison') return {
    type,
    plans: ['C4P Standard', 'C4P Advanced', 'C4P MTPro'],
    colors: ['#0891b2', '#3b82f6', '#7c3aed'],
    rows: [{ label: '', values: ['', '', ''] }],
  };
  if (type === 'scenario_cards') return {
    type,
    cards: [{ label: 'A', scenario: '', recommendation: '', color: 'standard' }],
  };
  return { type, content: '' };
}

// ── Simple block editor ───────────────────────────────────────────────────────
function SimpleBlockEditor({ block, onChange }) {
  const isComplex = COMPLEX_TYPES.includes(block.type);
  const [jsonStr, setJsonStr] = useState(() => JSON.stringify(block, null, 2));
  const [jsonErr, setJsonErr] = useState('');

  if (isComplex) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Code size={13} style={{ color: ORANGE }} />
          <span className="text-xs font-semibold text-orange-600">JSON editor - {block.type}</span>
        </div>
        <textarea
          className="w-full border rounded-lg px-3 py-2 font-mono text-xs resize-none"
          style={{ minHeight: '180px', color: jsonErr ? '#dc2626' : '#334155' }}
          value={jsonStr}
          onChange={e => {
            setJsonStr(e.target.value);
            try { const parsed = JSON.parse(e.target.value); onChange(parsed); setJsonErr(''); }
            catch { setJsonErr('Invalid JSON'); }
          }}
        />
        {jsonErr && <p className="text-xs text-red-600">{jsonErr}</p>}
      </div>
    );
  }

  if (block.type === 'email_template') return (
    <div className="space-y-2">
      <div className="space-y-1"><Label className="text-xs">Subject line</Label>
        <Input value={block.subject || ''} onChange={e => onChange({ ...block, subject: e.target.value })} placeholder="Email subject…" className="h-8 text-sm" /></div>
      <div className="space-y-1"><Label className="text-xs">Body</Label>
        <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
          style={{ minHeight: '140px' }} value={block.content || ''}
          onChange={e => onChange({ ...block, content: e.target.value })} placeholder="Email body…" /></div>
    </div>
  );

  if (block.type === 'bullets' || block.type === 'numbered') {
    const items = (block.items || []);
    const updateItem = (i, text) => onChange({ ...block, items: items.map((it, idx) => idx === i ? { ...it, text } : it) });
    const toggleItem = (i) => onChange({ ...block, items: items.map((it, idx) => idx === i ? { ...it, active: !it.active } : it) });
    const addItem = () => onChange({ ...block, items: [...items, { text: '', active: true }] });
    const removeItem = (i) => onChange({ ...block, items: items.filter((_, idx) => idx !== i) });
    return (
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <button type="button" onClick={() => toggleItem(i)} className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded"
              style={{ background: item.active !== false ? '#dcfce7' : '#f1f5f9', color: item.active !== false ? '#166534' : '#64748b' }}>
              {item.active !== false ? 'On' : 'Off'}
            </button>
            <Input value={item.text || ''} onChange={e => updateItem(i, e.target.value)}
              className="flex-1 h-8 text-sm" placeholder={`Item ${i + 1}…`} />
            <button onClick={() => removeItem(i)} className="flex-shrink-0 p-1 rounded hover:bg-red-50" style={{ color: '#dc2626' }}><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={addItem} className="text-xs px-2 py-1 rounded border" style={{ color: NAVY }}>+ Add item</button>
      </div>
    );
  }

  return (
    <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
      style={{ minHeight: block.type === 'heading' ? '48px' : '80px' }}
      value={block.content || ''} placeholder={`${block.type} content…`}
      onChange={e => onChange({ ...block, content: e.target.value })} />
  );
}

// ── Block row in editor ───────────────────────────────────────────────────────
function BlockRow({ block, index, total, onChange, onRemove, onMove }) {
  const [expanded, setExpanded] = useState(true);
  const typeLabel = BLOCK_TYPES.find(t => t.value === block.type)?.label || block.type;
  const isComplex = COMPLEX_TYPES.includes(block.type);
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2" style={{ background: 'hsl(var(--muted))' }}>
        <button onClick={() => setExpanded(e => !e)} className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-2 py-0.5 rounded"
              style={{ background: isComplex ? '#fff7ed' : '#f1f5f9', color: isComplex ? ORANGE : '#64748b' }}>
              {typeLabel}
            </span>
            <span className="text-xs truncate max-w-xs" style={{ color: '#64748b' }}>
              {block.content?.slice(0, 60) || block.subject || ''}
            </span>
          </div>
        </button>
        <div className="flex gap-0.5 flex-shrink-0">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onMove(index, -1)} disabled={index === 0}><ChevronUp size={12} /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onMove(index, 1)} disabled={index === total - 1}><ChevronDown size={12} /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" style={{ color: '#dc2626' }} onClick={() => onRemove(index)}><Trash2 size={12} /></Button>
        </div>
      </div>
      {expanded && block.type !== 'divider' && (
        <div className="p-3">
          <SimpleBlockEditor block={block} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

// ── Main editor page ──────────────────────────────────────────────────────────
export default function ResourceEditorPage() {
  const { id } = useParams(); // undefined = new resource
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading]   = useState(!isNew);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({
    title: '', category: 'Process Guides', description: '', slug: '', status: 'draft',
  });
  const [blocks, setBlocks]     = useState([]);
  const [addType, setAddType]   = useState('paragraph');

  useEffect(() => {
    if (isNew) return;
    resourcesAPI.get(id)
      .then(r => {
        const d = r.data;
        setForm({ title: d.title, category: d.category, description: d.description || '', slug: d.slug || '', status: d.status });
        setBlocks(d.content_blocks || []);
      })
      .catch(() => toast.error('Failed to load resource'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim(),
        slug: form.slug.trim() || autoSlug(form.title),
        tags: [],
        status: form.status,
        content_blocks: blocks,
      };
      if (isNew) {
        const r = await resourcesAPI.create(payload);
        toast.success('Resource created');
        navigate(`/admin/resources/${r.data.slug || r.data.id}/edit`);
      } else {
        await resourcesAPI.update(id, payload);
        toast.success('Resource saved');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addBlock = () => {
    setBlocks(b => [...b, newBlock(addType)]);
  };
  const updateBlock = (i, updated) => setBlocks(b => b.map((bl, idx) => idx === i ? updated : bl));
  const removeBlock = (i) => setBlocks(b => b.filter((_, idx) => idx !== i));
  const moveBlock = (i, dir) => {
    setBlocks(b => {
      const arr = [...b];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full rounded-xl" /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/resources')}><ArrowLeft size={14} /> Resources</Button>
          <span style={{ color: 'hsl(var(--muted-fg))' }}>/</span>
          <span className="text-sm font-semibold" style={{ color: NAVY }}>{isNew ? 'New resource' : form.title || 'Edit resource'}</span>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="outline" size="sm" onClick={() => window.open(`/resources/${form.slug || id}`, '_blank')}
              className="gap-1.5"><Eye size={13} /> Preview</Button>
          )}
          <Button onClick={handleSave} disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-1.5">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <div className="rounded-xl border bg-white p-5 space-y-4">
        <h3 className="font-semibold text-sm" style={{ color: NAVY }}>Resource details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2"><Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value, slug: f.slug || autoSlug(e.target.value) }))} placeholder="e.g. Which C4P plan is right for your customer?" /></div>
          <div className="space-y-1.5"><Label>Category</Label>
            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="published">Published - visible to PSMs</SelectItem>
                <SelectItem value="draft">Draft - hidden from PSMs</SelectItem>
                <SelectItem value="archived">Archived - hidden, retained</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="One-sentence summary shown on the resource card." /></div>
          <div className="space-y-1.5"><Label>Slug <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>- /resources/[slug]</span></Label>
            <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} placeholder="auto-generated from title" className="font-mono text-sm" /></div>
        </div>
      </div>

      {/* Block editor */}
      <div className="rounded-xl border bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm" style={{ color: NAVY }}>Content blocks ({blocks.length})</h3>
        </div>
        {blocks.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No content blocks yet. Add your first block below.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {blocks.map((block, i) => (
              <BlockRow key={i} block={block} index={i} total={blocks.length}
                onChange={updated => updateBlock(i, updated)}
                onRemove={() => removeBlock(i)}
                onMove={moveBlock} />
            ))}
          </div>
        )}
        {/* Add block controls */}
        <div className="flex gap-2 flex-wrap items-center pt-2" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          <Select value={addType} onValueChange={setAddType}>
            <SelectTrigger className="w-52 text-sm h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BLOCK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={addBlock} style={{ background: ORANGE, color: 'white' }} className="gap-1.5 h-9">
            <Plus size={13} /> Add block
          </Button>
        </div>
      </div>
    </div>
  );
}
